import type { LocaleCode } from '@/pages/cogo-marketing/i18n/locales'

/**
 * The creator-facing names for what a block is made of.
 *
 * Deliberately not the schema's own words: `datasource` and `variables` are
 * what the files are called, "쓰는 데이터" and "기억하는 것" are what they are.
 * The schema vocabulary is canonical for the DATA (so a bot's answer needs no
 * translation); this is the vocabulary for the READER.
 */
export interface BlockViewCopy {
  title: string
  empty: string
  blocksHeading: string
  whatItIs: string
  whenPressed: string
  runsNothing: string
  usesData: string
  remembers: string
  nothingHere: string
  pressLabel: (role: string, event: string) => string
}

const EN: BlockViewCopy = {
  title: 'What this app is made of',
  empty: 'Open a project to see its blocks.',
  blocksHeading: 'Blocks',
  whatItIs: 'What it is',
  whenPressed: 'When something is pressed',
  runsNothing: 'runs nothing — no flow by that name in this block',
  usesData: 'Data it uses',
  remembers: 'What it remembers',
  nothingHere: 'None',
  pressLabel: (role, event) => `${role} · ${event}`,
}

const KO: BlockViewCopy = {
  title: '이 앱은 무엇으로 이루어져 있나',
  empty: '프로젝트를 열면 블록이 보입니다.',
  blocksHeading: '블록',
  whatItIs: '무엇인가',
  whenPressed: '누르면 무슨 일이 일어나나',
  runsNothing: '아무것도 실행되지 않습니다 — 이 블록에 그 이름의 flow가 없습니다',
  usesData: '쓰는 데이터',
  remembers: '기억하는 것',
  nothingHere: '없음',
  pressLabel: (role, event) => `${role} · ${event}`,
}

const JA: BlockViewCopy = {
  title: 'このアプリは何でできているか',
  empty: 'プロジェクトを開くとブロックが表示されます。',
  blocksHeading: 'ブロック',
  whatItIs: '何か',
  whenPressed: '押すと何が起きるか',
  runsNothing: '何も実行されません — このブロックにその名前のフローがありません',
  usesData: '使うデータ',
  remembers: '覚えていること',
  nothingHere: 'なし',
  pressLabel: (role, event) => `${role} · ${event}`,
}

const ZH: BlockViewCopy = {
  title: '这个应用由什么组成',
  empty: '打开项目后即可看到其区块。',
  blocksHeading: '区块',
  whatItIs: '这是什么',
  whenPressed: '按下时会发生什么',
  runsNothing: '不会执行任何操作 — 此区块中没有该名称的流程',
  usesData: '使用的数据',
  remembers: '记住的内容',
  nothingHere: '无',
  pressLabel: (role, event) => `${role} · ${event}`,
}

const ZH_TW: BlockViewCopy = {
  title: '這個應用由什麼組成',
  empty: '開啟專案後即可看到其區塊。',
  blocksHeading: '區塊',
  whatItIs: '這是什麼',
  whenPressed: '按下時會發生什麼',
  runsNothing: '不會執行任何操作 — 此區塊中沒有該名稱的流程',
  usesData: '使用的資料',
  remembers: '記住的內容',
  nothingHere: '無',
  pressLabel: (role, event) => `${role} · ${event}`,
}

/** Typed per locale, so the compiler is the key-parity gate — not a reviewer. */
export const BLOCK_VIEW_COPY: Record<LocaleCode, BlockViewCopy> = {
  en: EN,
  ko: KO,
  ja: JA,
  zh: ZH,
  'zh-TW': ZH_TW,
}
